/* ============================================================================
   Formatting, in Indian conventions.
   ----------------------------------------------------------------------------
   Here rather than in either dashboard because BOTH read these numbers to the
   same people, and two copies drift: change `maximumFractionDigits` in one and
   the office and the customer are looking at different totals for the same
   invoice. No Zod in this file, so browser code can import it from
   `@mys/contracts/format` without dragging the schemas in — the same rule as
   constants.ts.

   Neither choice is cosmetic. `09/09/2026` means two different days depending
   on where the reader is, so `dateStyle: 'medium'` spells the month out. And
   en-IN groups by lakh and crore — ₹1,00,000, not ₹100,000 — which is how
   every other number this audience reads all day is grouped. Getting it wrong
   makes a figure take a second look, and an invoice total is exactly where a
   second look should not be needed.
   ========================================================================== */
const DATE_TIME = new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
const DATE_ONLY = new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium' });
const RUPEES = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    // Every amount in this product is a whole number of rupees. Paise exist only
    // inside apps/api/src/integrations/payments and never reach a screen.
    maximumFractionDigits: 0,
});
export function formatDateTime(iso) {
    return DATE_TIME.format(new Date(iso));
}
export function formatDate(iso) {
    return DATE_ONLY.format(new Date(iso));
}
/** Rupees, grouped the Indian way. The input is already rupees, never paise. */
export function formatInr(amount) {
    return RUPEES.format(amount);
}
/**
 * A span of minutes, for the reply clock. `47m`, `2h 10m`, `1d 5h`.
 *
 * Here with the other formatters because the countdown on the leads board, the
 * reply time on a lead's own page and the share-replied figure on /analytics
 * are the same quantity rendered three times — and the one thing worse than an
 * ugly duration is two screens disagreeing about how long a shop waited.
 *
 * The largest two units only, and a zero lower unit is dropped: `2h`, not
 * `2h 0m`. Past a day it stops counting minutes, because at that point the
 * minutes are not the news.
 */
export function formatDuration(minutes) {
    const total = Math.max(0, Math.round(minutes));
    if (total < 60)
        return `${total}m`;
    const hours = Math.floor(total / 60);
    if (hours < 24) {
        const rest = total % 60;
        return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
    }
    const days = Math.floor(hours / 24);
    const rest = hours % 24;
    return rest === 0 ? `${days}d` : `${days}d ${rest}h`;
}
//# sourceMappingURL=format.js.map